import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TransactionFilter } from '@unistocks/features/portfolios/transactions/transaction-filter';
import {
  ListResultDtoOfLookupDtoOfGuid,
  PieChartType,
  SortOrder
} from '@unistocks/proxy/axios-client';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { RootState } from '@unistocks/shared/store';
import { clone, difference } from '@unistocks/shared/utils/util';

const defaultFilter: TransactionFilter = {
  type: null,
  status: undefined,
  minDate: undefined,
  maxDate: undefined,
  minAmount: undefined,
  maxAmount: undefined,
  sorting: {
    prop: 'date',
    direction: SortOrder.Descending
  },
  maxResultCount: MAX_RESULT_COUNT
};

export type PortfolioSlice = {
  selectedPortfolioId?: string;
  portfolios: {
    [key: string]: {
      transactionFilter: TransactionFilter;
      pieChart: PieChartType;
    };
  };
};

const initialState: PortfolioSlice = {
  selectedPortfolioId: undefined,
  portfolios: {}
};

const { reducer, actions, selectors, reducerPath } = createSlice({
  name: 'portfolio',
  reducerPath: 'portfolio',
  initialState,
  reducers: {
    syncPortfolioState: (state, action: PayloadAction<ListResultDtoOfLookupDtoOfGuid>) => {
      const { items } = action.payload;
      const portfolioIds = items.map(({ id }) => id);
      const deletedIds = difference(Object.keys(state.portfolios), portfolioIds);

      for (const id of portfolioIds) {
        if (!state.portfolios[id]) {
          state.portfolios[id] = {
            transactionFilter: clone(defaultFilter),
            pieChart: PieChartType.Categories
          };
        }
      }

      for (const id of deletedIds) {
        delete state.portfolios[id];
      }
    },

    setSelectedPortfolioId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedPortfolioId = action.payload;
    },

    setTransactionFilter: (
      state,
      action: PayloadAction<{ id: string; filter?: TransactionFilter }>
    ) => {
      const { id, filter } = action.payload;
      if (!state.portfolios[id]) {
        state.portfolios[id] = {
          transactionFilter: filter ?? clone(defaultFilter),
          pieChart: PieChartType.Categories
        };
      } else {
        state.portfolios[id].transactionFilter = filter ?? clone(defaultFilter);
      }
    },

    setPortfolioPieChartType: (
      state,
      action: PayloadAction<{ id: string; pieChart: PieChartType }>
    ) => {
      const { id, pieChart } = action.payload;
      if (!state.portfolios[id]) {
        state.portfolios[id] = {
          transactionFilter: clone(defaultFilter),
          pieChart
        };
      } else {
        state.portfolios[id].pieChart = pieChart;
      }
    },

    clear: () => ({ ...initialState })
  },
  selectors: {
    selectSelectedPortfolioId: state => state.selectedPortfolioId,
    selectTransactionFilter: (state, id: string) =>
      state.portfolios[id]?.transactionFilter ?? defaultFilter,
    selectPieChartType: (state, id: string) =>
      state.portfolios[id]?.pieChart ?? PieChartType.Categories
  }
});

export const PortfolioReducer = reducer;
export const PortfolioActions = actions;
export const PortfolioReducerPath = reducerPath;

export const { selectSelectedPortfolioId } = selectors;
export const selectTransactionFilter = (id: string) => (state: RootState) =>
  selectors.selectTransactionFilter(state, id);
export const selectPieChartType = (id: string) => (state: RootState) =>
  selectors.selectPieChartType(state, id);
